import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateLoader, TranslateModule, TranslateService, TranslateStore } from '@ngx-translate/core';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { LocalStorageModule, LocalStorageService } from 'angular-2-local-storage';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { SliderModule } from 'primeng/slider';
import { DialogService, DynamicDialogConfig, DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { PickListModule } from 'primeng/picklist';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputNumberModule } from 'primeng/inputnumber';
import { StepsModule } from 'primeng/steps';
import { ChipModule } from 'primeng/chip';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipsModule } from 'primeng/chips';
import { SlideMenuModule } from 'primeng/slidemenu';
import { AgilityRiskComponent } from './agility-risk.component';
import { AgilityRiskRoutingModule } from './agility-risk-routing.module';
import { AGILITY_RISK_MODULE_CONFIG, RiskModuleConfig } from './agility-risk.config.interface';
import { languages, permissions } from './agility-risk.constants';
import { AgilityRiskFilterService, FilterDummyService } from './providers/agility-risk-filter.service';
import { AgilityCustomTranslateLoaderService } from './providers/agility-custom-translate-loader.service';
import { provideThemeServiceConfig, ThemeServiceConfig } from './providers/agility-risk-theme.service';
import { AgilityRiskService, RiskDummyService } from './providers/agility-risk.service';
import { AgilityDatetimePipe } from './pipes/agility-datetime.pipe';
import { AgilityPercentPipe } from './pipes/agility-percent.pipe';
import { RiskDistributionComponent } from './components/risk-distribution/risk-distribution.component';
import { RiskTemplatesComponent } from './components/risk-templates/risk-templates.component';
import { AgilityRiskTestComponent } from './components/test/agility-risk-test.component';
import { RiskDashboardComponent } from './components/risk-dashboard/risk-dashboard.component';
import { ThreatTypesComponent } from './components/risk-threat-types/threat-types.component';
import { ConfirmationModalComponent } from './components/shared/modals/confirmation-modal/confirmation-modal.component';
import { RiskBreadcrumbComponent } from './components/shared/risk-breadcrumb/risk-breadcrumb.component';
import { UpsertThreatTypeModalComponent } from './components/shared/modals/upsert-threat-type-modal/upsert-threat-type-modal.component';
import { RiskAssessmentSurveyDefinitionsModalComponent } from './components/risk-assessment/survey/modals/risk-assessment-survey-definitions-modal/risk-assessment-survey-definitions-modal.component';
import { RiskAssessmentSurveyDetailsModalComponent } from './components/risk-assessment/survey/modals/risk-assessment-survey-details-modal/risk-assessment-survey-details-modal.component';
import { RiskAssessmentSurveyComponent } from './components/risk-assessment/survey/risk-assessment-survey.component';
import { RiskAssessmentSurveyCustomFieldsModalComponent } from './components/risk-assessment/survey/modals/risk-assessment-survey-custom-fields-modal/risk-assessment-survey-custom-fields-modal.component';
import { AgilityRiskSetupComponent } from './components/risk-setup/agility-risk-setup.component';
import { SurveyBooleanQuestionComponent } from './components/risk-assessment/survey/components/survey-boolean-question/survey-boolean-question.component';
import { SurveyRiskFactorQuestionComponent } from './components/risk-assessment/survey/components/risk-factor-question/survey-risk-factor-question.component';
import { AgilityRiskPermissionService, PermissionDummyService } from './providers/agility-risk-permission.service';
import { NavigationServiceConfig, provideNavigationServiceConfig } from './providers/agility-risk-navigation.service';
import { RiskAssessmentComponent } from './components/risk-assessment/risk-assessment.component';
import { RiskDistributionThreatsModalComponent } from './components/risk-distribution/modals/risk-distribution-threats-modal/risk-distribution-threats-modal.component';
import { RiskAssessmentSummaryComponent } from './components/risk-assessment/summary/risk-assessment-summary.component';
import { SelectUsersModalComponent } from './components/shared/modals/select-users-modal/select-users-modal.component';
import { RiskFilterComponent } from './components/shared/risk-filter/risk-filter.component';
import { RiskFiltersComponent } from './components/shared/risk-filter/risk-filters/risk-filters.component';
import { RiskFormulaManagementComponent } from './components/risk-formula-management/risk-formula-management.component';
import { RiskAssessmentMemberAnswersModalComponent } from './components/risk-assessment/survey/modals/risk-assessment-member-answers-modal/risk-assessment-member-answers-modal.component';
import { FormulaDescriptionComponent } from './components/risk-formula-management/formula-description/formula-description.component';
import { RiskFactorsModalComponent } from './components/risk-formula-management/modals/risk-factors-modal/risk-factors-modal.component';
import { RiskFactorModalComponent } from './components/risk-formula-management/modals/risk-factor-modal/risk-factor-modal.component';
import { TestFormulaModalComponent } from './components/risk-formula-management/modals/test-formula-modal/test-formula-modal.component';
import { AdvancedRiskFormulaManagementComponent } from './components/risk-formula-management/advanced-risk-formula-management/advanced-risk-formula-management.component';
import { AddRemoveRiskBandsComponent } from './components/risk-formula-management/modals/add-remove-risk-bands/add-remove-risk-bands.component';
import { DateTimeServiceConfig, provideDateTimeServiceConfig } from './providers/agility-risk-datetime.service';
import { SpinnerComponent } from './components/shared/spinner/spinner.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RiskFormulaBuilderComponent } from './components/risk-formula-management/risk-formula-builder/risk-formula-builder.component';
import { RiskBandSliderComponent } from './components/risk-formula-management/risk-band-slider/risk-band-slider.component';
import { RiskAssessmentCompleteComponent } from './components/risk-assessment/complete/risk-assessment-complete.component';
import { AgilityDatePipe } from './pipes/agility-date.pipe';
import { ThreatQuestionsComponent } from './components/risk-templates/risk-template-management/threat-questions/threat-questions.component';
import { UpsertThreatQuestionModalComponent } from './components/shared/modals/upsert-threat-question-modal/upsert-threat-question-modal.component';
import { ConfigureConditionalLogicModalComponent } from './components/shared/modals/configure-conditional-logic-modal/configure-conditional-logic-modal.component';
import { RiskTemplateThreatsComponent } from './components/risk-templates/risk-template-management/risk-template-threats/risk-template-threats.component';
import { RiskTemplateManagementComponent } from './components/risk-templates/risk-template-management/risk-template-management/risk-template-management.component';
import { AddEditThreatComponent } from './components/risk-templates/modals/add-edit-threat/add-edit-threat.component';
import { RiskAssessmentDetailsComponent } from './components/risk-assessment/details/risk-assessment-details.component';
import { AssessmentDetailAdditionalCommentsModalComponent } from './components/risk-assessment/details/modals/assessment-detail-additional-comments-modal/assessment-detail-additional-comments-modal.component';
import { RiskAssessmentDetailsCustomFieldsModalComponent } from './components/risk-assessment/details/modals/risk-assessment-details-custom-fields-modal/risk-assessment-details-custom-fields-modal.component';
import { RiskAssessmentRiskMemberAnswersModalComponent } from './components/risk-assessment/details/modals/risk-assessment-risk-member-answers-modal/risk-assessment-risk-member-answers-modal.component';
import { CopyThreatsFromTemplateComponent } from './components/risk-templates/modals/copy-threats-from-template/copy-threats-from-template.component';
import { RiskTemplateHeaderComponent } from './components/risk-templates/risk-template-management/risk-template-header/risk-template-header.component';
import { BaseComponent } from './components/shared/base/base.component';
import { CustomFieldsManagementComponent } from './components/custom-fields-management/custom-fields-management.component';
import { AccessManagerComponent } from './components/risk-templates/risk-template-access/access-manager/access-manager.component';
import { AccessManagerStepsComponent } from './components/risk-templates/risk-template-access/access-manager-steps/access-manager-steps.component';
import { RiskTemplateAccessComponent } from './components/risk-templates/risk-template-access/risk-template-access.component';
import { AccessManagerContentComponent } from './components/risk-templates/risk-template-access/access-manager-content/access-manager-content.component';
import { AgilityRiskSpinnerService, SpinnerDefaultService } from './providers/agility-risk-spinner.service';
import { AgilityRiskMessageService, MessageDefaultService } from './providers/agility-risk-message.service';
import { SkeletonModule } from 'primeng/skeleton';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { DistributionSendAssessmentNameModalComponent } from './components/risk-distribution/modals/distribution-send-assessment-name-modal/distribution-send-assessment-name-modal.component';
import { ListboxModule } from 'primeng/listbox';
import { ControlFieldErrorMessageComponent } from './components/shared/control-field-error-message/control-field-error-message.component';
import { RiskFormulaListComponent } from './components/risk-formula-management/risk-formula-list/risk-formula-list.component';
import { PermissionDirective } from './directives/permission.directive';
import { RiskTemplatesListComponent } from './components/risk-templates/risk-template-access/modals/risk-templates-list/risk-templates-list.component';
import { AffectedAreasListComponent } from './components/risk-templates/risk-template-access/modals/affected-areas-list/affected-areas-list.component';
import { ButtonUnFocusDirective } from './directives/buttonUnFocusDirective.directive';

export function createTranslateLoader(storage: LocalStorageService): AgilityCustomTranslateLoaderService {
  return new AgilityCustomTranslateLoaderService(storage);
}

@NgModule({
  declarations: [
    ButtonUnFocusDirective,
    AgilityRiskComponent,
    AgilityRiskSetupComponent,
    AgilityRiskTestComponent,
    ConfirmationModalComponent,
    RiskAssessmentComponent,
    RiskAssessmentMemberAnswersModalComponent,
    RiskAssessmentSummaryComponent,
    RiskAssessmentSurveyComponent,
    RiskAssessmentSurveyCustomFieldsModalComponent,
    RiskAssessmentSurveyDefinitionsModalComponent,
    RiskAssessmentSurveyDetailsModalComponent,
    RiskBreadcrumbComponent,
    RiskDashboardComponent,
    RiskDistributionComponent,
    RiskDistributionThreatsModalComponent,
    RiskFilterComponent,
    RiskFiltersComponent,
    RiskFormulaManagementComponent,
    RiskTemplatesComponent,
    SelectUsersModalComponent,
    SpinnerComponent,
    SurveyBooleanQuestionComponent,
    SurveyRiskFactorQuestionComponent,
    ThreatTypesComponent,
    UpsertThreatTypeModalComponent,
    FormulaDescriptionComponent,
    RiskFactorsModalComponent,
    RiskFactorModalComponent,
    TestFormulaModalComponent,
    AdvancedRiskFormulaManagementComponent,
    AddRemoveRiskBandsComponent,
    AgilityDatetimePipe,
    AgilityDatePipe,
    SpinnerComponent,
    AddRemoveRiskBandsComponent,
    RiskFormulaBuilderComponent,
    RiskBandSliderComponent,
    RiskAssessmentCompleteComponent,
    ThreatQuestionsComponent,
    UpsertThreatQuestionModalComponent,
    ConfigureConditionalLogicModalComponent,
    AgilityPercentPipe,
    RiskTemplateThreatsComponent,
    RiskAssessmentCompleteComponent,
    RiskTemplateManagementComponent,
    AddEditThreatComponent,
    RiskAssessmentDetailsComponent,
    AssessmentDetailAdditionalCommentsModalComponent,
    RiskAssessmentRiskMemberAnswersModalComponent,
    RiskAssessmentDetailsCustomFieldsModalComponent,
    CopyThreatsFromTemplateComponent,
    RiskTemplateHeaderComponent,
    CustomFieldsManagementComponent,
    RiskTemplateAccessComponent,
    BaseComponent,
    CustomFieldsManagementComponent,
    AccessManagerComponent,
    AccessManagerStepsComponent,
    AccessManagerContentComponent,
    DistributionSendAssessmentNameModalComponent,
    ControlFieldErrorMessageComponent,
    RiskFormulaListComponent,
    PermissionDirective,
    RiskTemplatesListComponent,
    AffectedAreasListComponent
  ],
  imports: [
    CommonModule,
    TableModule,
    HttpClientModule,
    AgilityRiskRoutingModule,
    LocalStorageModule.forRoot({
      prefix: 'agilityRisk',
      storageType: 'localStorage'
    }),
    TranslateModule.forChild({
      isolate: true,
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [LocalStorageService]
      },
    }),
    NgxPermissionsModule.forRoot(),
    ProgressBarModule,
    DialogModule,
    DynamicDialogModule,
    ProgressSpinnerModule,
    FormsModule,
    BreadcrumbModule,
    TooltipModule,
    AccordionModule,
    SliderModule,
    ChartModule,
    DropdownModule,
    PickListModule,
    MultiSelectModule,
    ButtonModule,
    RadioButtonModule,
    CalendarModule,
    ReactiveFormsModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    BadgeModule,
    ToastModule,
    CardModule,
    ChipModule,
    ChipsModule,
    SlideMenuModule,
    AutoCompleteModule,
    CheckboxModule,
    StepsModule,
    ListboxModule,
    SkeletonModule,
    MessagesModule,
    MessageModule
  ],
  exports: [
    AgilityRiskComponent,
    AgilityRiskSetupComponent,
    ConfirmationModalComponent,
    RiskAssessmentSummaryComponent,
    RiskAssessmentSurveyComponent,
    RiskAssessmentSurveyCustomFieldsModalComponent,
    RiskAssessmentSurveyDefinitionsModalComponent,
    RiskBreadcrumbComponent,
    RiskDashboardComponent,
    RiskDistributionThreatsModalComponent,
    ThreatTypesComponent,
    UpsertThreatTypeModalComponent,
    RiskAssessmentComponent,
    RiskAssessmentSurveyDetailsModalComponent,
    RiskDistributionComponent,
    RiskFormulaManagementComponent,
    RiskTemplatesComponent,
    AddRemoveRiskBandsComponent
  ],
  providers: [
    DynamicDialogConfig,
    DialogService,
    TranslateStore,
    MessageService,
    DatePipe
  ]
})

export class AgilityRiskModule {
  constructor(private translate: TranslateService,
              private ngxPermissionService: NgxPermissionsService,
              private permissionService: AgilityRiskPermissionService) {
    this.initPermissions();
    translate.setDefaultLang(languages.english);
    registerLocaleData(localeEn);
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);
    registerLocaleData(localePt);
  }

  static forRoot(config: RiskModuleConfig = {}): ModuleWithProviders<AgilityRiskModule> {
    return {
      ngModule: AgilityRiskModule,
      providers: [
        config.serviceProvider || {provide: AgilityRiskService, useClass: RiskDummyService},
        config.permissionProvider || {provide: AgilityRiskPermissionService, useClass: PermissionDummyService},
        config.filterProvider || {provide: AgilityRiskFilterService, useClass: FilterDummyService},
        config.spinnerProvider || {provide: AgilityRiskSpinnerService, useClass: SpinnerDefaultService},
        config.messageProvider || {provide: AgilityRiskMessageService, useClass: MessageDefaultService},
        {provide: AGILITY_RISK_MODULE_CONFIG, useValue: config},
        {
          provide: ThemeServiceConfig,
          useFactory: provideThemeServiceConfig,
          deps: [AGILITY_RISK_MODULE_CONFIG]
        },
        {
          provide: NavigationServiceConfig,
          useFactory: provideNavigationServiceConfig,
          deps: [AGILITY_RISK_MODULE_CONFIG]
        },
        {
          provide: DateTimeServiceConfig,
          useFactory: provideDateTimeServiceConfig,
          deps: [AGILITY_RISK_MODULE_CONFIG]
        }
      ]
    };
  }

  private initPermissions() {
    this.ngxPermissionService.flushPermissions();
    this.ngxPermissionService.addPermission(permissions.canAccessRisk, () => {
      return this.permissionService.canAccessRisk;
    });
    this.ngxPermissionService.addPermission(permissions.isSuperAdmin, () => {
      return this.permissionService.isSuperAdmin;
    });
    this.ngxPermissionService.addPermission(permissions.isPartner, () => {
      return this.permissionService.isPartner;
    });
    this.ngxPermissionService.addPermission(permissions.isAdmin, () => {
      return this.permissionService.isAdmin;
    });
    this.ngxPermissionService.addPermission(permissions.isManager, () => {
      return this.permissionService.isManager;
    });
    this.ngxPermissionService.addPermission(permissions.isUser, () => {
      return this.permissionService.isUser;
    });
    this.ngxPermissionService.addPermission(permissions.isListed, () => {
      return this.permissionService.isListed;
    });
    this.ngxPermissionService.addPermission(permissions.hasAdminRoles, () => {
      return this.permissionService.hasAdminRoles;
    });
    this.ngxPermissionService.addPermission(permissions.hasManagerRoles, () => {
      return this.permissionService.hasManagerRoles;
    });
  }
}
